import { addToaster, hideLoader, showLoader } from '@app-store'
import React, { useEffect, useMemo } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import {
  denormalizeLookups,
  normalizeLookups,
} from '../../utils/hybridFormRendererfuntions.utils'
import { editEntityData } from '../../apis/entityData.api'
import { RECORD_STATUS } from '../../constants/form.constants'
import { isDraftSubmission } from '../../utils/form.utils'
import { HybridLayoutActions } from './HybridLayoutActions'
import HybridLayoutParser from './HybridLayoutParser'
import { v4 as uuid } from 'uuid'
import { useAppDispatch } from '../../utils/agent.store'
import { HybridLayoutPropTypes } from './hybridLayout.types'
import { STRINGS } from '../../utils/strings'
import { isEmpty } from 'lodash'
import { removePhoneMasking } from '@ui-kit'
import { get } from 'lodash'
import {
  ENTITY_FIELDS,
  ENTITY_NAME,
  HYBRID_LAYOUT_CONSTANTS,
} from '../../utils/constants'
import {
  hasHybridLayoutFormRules,
  HybridFormBehaviorScope,
  triggerHybridFormBehaviorHydrate,
} from '../../form-behavior/HybridFormBehaviorScope'

const HybridLayout = ({
  layoutData,
  recordId,
  isWorkflow,
  entityData,
  handleFormSubmit,
  handleDraftSave,
  formHeaders,
  openAllAccordions = false,
  hideHeaders = false,
  mode,
  task,
  isDraft,
}: HybridLayoutPropTypes) => {
  const methods = useForm()
  const { getValues, trigger, formState, setValue, reset } = methods

  const dispatch = useAppDispatch()
  const layoutUid = layoutData?.uid
  const hasFormRules = hasHybridLayoutFormRules(layoutData)
  const [refetchTimer, setRefetchTimer] = React.useState(0)
  const versionError = get(
    formState?.errors,
    `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS?.CASE_PAP_DETAILS?.acceptable_application_version}`
  )
  const refetchLogic = () => {
    setRefetchTimer((prev) => prev + 1)
  }

  const onSubmit = async (data: FieldValues) => {
    if (recordId) {
      data.id = recordId
    }
    if (isWorkflow) {
      return
    } else {
      dispatch(showLoader())
      try {
        const response = await editEntityData(
          layoutData?.entity_name,
          layoutData?.entity_id,
          Number(layoutData?.id),
          data,
          data.id
        )
        if (response?.data) {
          dispatch(
            addToaster({
              message: STRINGS.DATA_UPDATED_SUCCESSFULLY,
              type: 'success',
              id: uuid(),
            })
          )
          refetchLogic()
        }
      } catch (e) {
        console.log('Error updating data:', e)
      } finally {
        dispatch(hideLoader())
      }
    }
  }
  const formUniqueId = useMemo(() => {
    return `lookup-modal-${uuid()}`
  }, [])

  const getNestedFields = (fields: string[], parentKey: string): string[] => {
    return fields
      ?.filter((field) => field?.startsWith(`${parentKey}.`))
      ?.map((field) => field?.replace(`${parentKey}.`, ''))
  }

  const generateNewFinalData = (
    value: FieldValues,
    finalData: FieldValues,
    phoneFields: string[]
  ) => {
    const result: FieldValues = Array.isArray(finalData) ? [] : {}

    for (const key in finalData) {
      const finalVal = finalData[key]
      const valueVal = value?.[key]

      const nestedFields = getNestedFields(phoneFields, key)
      if (isEmpty(finalVal) && !isEmpty(valueVal)) {
        result[key] =
          typeof valueVal === 'object' && valueVal !== null
            ? { ...valueVal, _state: 'REMOVED' }
            : typeof valueVal !== 'object' && finalVal === null
            ? finalVal
            : valueVal
      } else if (
        typeof finalVal === 'object' &&
        finalVal !== null &&
        typeof valueVal === 'object' &&
        valueVal !== null &&
        !Array.isArray(finalVal)
      ) {
        result[key] = generateNewFinalData(
          valueVal,
          finalVal,
          nestedFields ?? []
        )
      } else if (
        phoneFields &&
        phoneFields?.includes(key) &&
        typeof finalVal === 'string'
      ) {
        result[key] = removePhoneMasking(finalVal)
      } else if (key !== 'id' && finalVal === valueVal) {
        // empty Statement
        continue
      } else {
        result[key] = finalVal
      }
    }

    return result
  }

  useEffect(() => {
    if (entityData) {
      const normalizedData = normalizeLookups(entityData)
      reset(normalizedData)
      if (hasFormRules && layoutUid) {
        triggerHybridFormBehaviorHydrate(layoutUid)
      }
    }
  }, [entityData, reset, hasFormRules, layoutUid])

  useEffect(() => {
    if (!isDraft?.current && versionError) {
      dispatch(
        addToaster({
          id: uuid(),
          type: 'error',
          message: `${STRINGS.APPLICATION_VERSION} is required`,
        })
      )
    }
  }, [versionError])

  const formContent = (
    <form
      className="mt-3"
      onSubmit={async (e) => {
        e.stopPropagation()
        e.preventDefault()

        const isValid = await trigger()

        if (!isValid && !isDraft?.current) {
          return
        }
        const value = getValues()
        const clonedData = structuredClone(value)
        delete clonedData?.lookupLabels
        delete clonedData?.__ERROR_KEY__
        delete clonedData?.__FORM_ORIGINAL_VALUES_EXTRA_FIELD__
        delete clonedData?.__FORM_ORIGINAL_VALUES_PHONE_FIELDS__
        const denormalized = denormalizeLookups(clonedData)

        const finaldata = denormalized
        delete finaldata?.__ERROR_KEY__
        delete finaldata?.__FORM_ORIGINAL_VALUES_EXTRA_FIELD__

        const newFinalData = generateNewFinalData(
          value?.__FORM_ORIGINAL_VALUES_EXTRA_FIELD__,
          finaldata,
          value?.__FORM_ORIGINAL_VALUES_PHONE_FIELDS__
        )
        if (newFinalData?.products?.trademarked_name) {
          delete newFinalData.products.trademarked_name
        }
        if (isDraftSubmission(e)) {
          newFinalData.record_status = RECORD_STATUS.DRAFT
          if (handleDraftSave) {
            handleDraftSave?.(newFinalData)
          } else {
            onSubmit(newFinalData)
          }
        } else {
          newFinalData.record_status = RECORD_STATUS.ACTIVE
          if (handleFormSubmit) {
            handleFormSubmit?.(newFinalData)
          } else {
            onSubmit(newFinalData)
          }
        }
      }}
    >
      {hideHeaders ? null : formHeaders || <HybridLayoutActions />}

      <HybridLayoutParser
        layoutData={layoutData}
        recordId={recordId}
        formUniqueId={formUniqueId}
        refetchTimer={refetchTimer}
        refetchLogic={refetchLogic}
        openAllAccordions={openAllAccordions}
        mode={mode}
        task={task}
      />
    </form>
  )

  return (
    <React.Fragment>
      <div id={formUniqueId} />
      <FormProvider {...methods}>
        <HybridFormBehaviorScope
          layout={layoutData}
          formUniqueId={formUniqueId}
          setValue={setValue}
          getValues={getValues}
          reset={reset}
          mode={mode}
        >
          {formContent}
        </HybridFormBehaviorScope>
      </FormProvider>
    </React.Fragment>
  )
}

export default HybridLayout
